import React, { useState } from "react";
import { Icon } from "@mui/material";
import {
  DatePicker as DatePickerBase,
  LocalizationProvider,
} from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment, { type Moment } from "moment";
import RpSvgIcon from "@/components/icons/RpSvgIcon";
import { createDatePickerInputClickHandler } from "@/helpers/utils";
import { resolveRadiusReference } from "@/config/themeTokens";

type DateValue = Moment | string | null | undefined;

type RpDatePickerProps = {
  value?: DateValue;
  onChange?: (_value: Moment | null) => void;
  disabled?: boolean;
  placeholder?: string;
  label?: string;
  minDate?: DateValue;
  maxDate?: DateValue;
  shouldDisableDate?: (_date: Moment) => boolean;
  disablePast?: boolean;
  format?: string;
  smallSize?: boolean;
  size?: "small" | "medium";
  open?: boolean;
  onOpenChange?: (_open: boolean) => void;
  slots?: Record<string, unknown>;
  containerClassName?: string;
  borderColor?: string;
  borderRadius?: string;
  calenderIcon?: boolean;
  [key: string]: unknown;
};

const DatePicker = DatePickerBase as unknown as React.ComponentType<
  Record<string, unknown>
>;

const RpDatePicker = ({
  value,
  onChange,
  disabled = false,
  placeholder = "Select Date",
  label = "",
  minDate,
  maxDate,
  shouldDisableDate,
  disablePast = true,
  format = "MMM DD YYYY",
  smallSize = false,
  size = "small",
  open: controlledOpen,
  onOpenChange,
  slots = {},
  containerClassName = "",
  borderColor = "",
  borderRadius = "var(--radius-control)",
  calenderIcon = false,
  ...rest
}: RpDatePickerProps) => {
  const [internalOpen, setInternalOpen] = useState(false);

  // Use controlled open state if provided, otherwise use internal state
  const isOpen = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const handleOpenChange = (open: boolean) => {
    if (onOpenChange) {
      onOpenChange(open);
    } else {
      setInternalOpen(open);
    }
  };

  // Convert string value to Moment object if needed
  const getMomentValue = (val: DateValue) => {
    if (!val) return null;
    if (moment.isMoment(val)) return val;
    if (typeof val === "string") {
      const momentVal = moment(val);
      return momentVal.isValid() ? momentVal : null;
    }
    return null;
  };

  // Convert minDate and maxDate to Moment objects if needed
  const getMomentDate = (val: DateValue) => {
    if (!val) return undefined;
    if (moment.isMoment(val)) return val;
    if (typeof val === "string") {
      const momentVal = moment(val);
      return momentVal.isValid() ? momentVal : undefined;
    }
    return undefined;
  };

  // Convert Moment object to string when onChange is called
  const handleChange = (newValue: Moment | null) => {
    if (onChange) {
      onChange(newValue);
    }
  };

  const handleInputClick = createDatePickerInputClickHandler(
    () => handleOpenChange(true),
    disabled,
    isOpen
  );

  return (
    <LocalizationProvider
      dateAdapter={AdapterMoment}
      localeText={{
        fieldMonthPlaceholder: () => "",
        fieldDayPlaceholder: () => "",
        fieldYearPlaceholder: () => "",
      }}
    >
      <div className={containerClassName} style={{ position: "relative" }}>
        <DatePicker
          showDaysOutsideCurrentMonth
          disableHighlightToday
          value={getMomentValue(value)}
          onChange={handleChange}
          disabled={disabled}
          disablePast={disablePast}
          minDate={getMomentDate(minDate)}
          maxDate={getMomentDate(maxDate)}
          shouldDisableDate={shouldDisableDate}
          format={format ? format : undefined}
          open={isOpen}
          onOpen={() => handleOpenChange(true)}
          onClose={() => handleOpenChange(false)}
          slots={{
            openPickerIcon: calenderIcon
              ? () => <Icon component={RpSvgIcon} name="month_calender" />
              : null,
            toolbar: () => null,
            ...slots,
          }}
          slotProps={{
            textField: {
              readOnly: true,
              variant: "outlined",
              size: size,
              fullWidth: true,
              disabled: disabled,
              onClick: handleInputClick,
              InputLabelProps: {
                shrink: true,
              },
              InputProps: {
                readOnly: true,
                inputProps: {
                  readOnly: true,
                  autoComplete: "off",
                  spellCheck: false,
                },
                sx: () => ({
                  "& input": {
                    cursor: "pointer",
                    userSelect: "none",
                    WebkitUserSelect: "none",
                    MozUserSelect: "none",
                    msUserSelect: "none",
                    "&::selection": {
                      background: "transparent !important",
                      color: "inherit !important",
                    },
                    "&::-moz-selection": {
                      background: "transparent !important",
                      color: "inherit !important",
                    },
                  },
                }),
              },
              sx: (theme: any) => ({
                color: disabled
                  ? (theme.vars || theme).palette.action.disabled
                  : (theme.vars || theme).palette.text.primary,
                backgroundColor: disabled
                  ? (theme.vars || theme).palette.action.disabledBackground
                  : (theme.vars || theme).palette.background.paper,
                borderRadius: resolveRadiusReference(
                  borderRadius,
                  "var(--radius-control)"
                ),
                ...(borderColor && {
                  border: `1px solid ${borderColor} !important`,
                }),

                "& *::selection": {
                  background: "transparent !important",
                  color: "inherit !important",
                },
                "& *::-moz-selection": {
                  background: "transparent !important",
                  color: "inherit !important",
                },
                "& .MuiPickersOutlinedInput-root": {
                  color: disabled
                    ? (theme.vars || theme).palette.action.disabled
                    : (theme.vars || theme).palette.text.primary,
                  height: "40px",
                  fontWeight: "500 !important",
                  userSelect: "none",
                  WebkitUserSelect: "none",
                  MozUserSelect: "none",
                  msUserSelect: "none",
                  ...(smallSize && {
                    fontSize: "12px !important",
                  }),
                },
              }),
            },
            openPickerButton: {
              disabled: disabled,
              sx: (theme: any) => ({
                color: disabled
                  ? (theme.vars || theme).palette.action.disabled
                  : (theme.vars || theme).palette.primary.main,
                padding: "2px",
                marginLeft: "-6px",
                minWidth: "auto",
              }),
            },
          }}
          {...rest}
        />
        {!getMomentValue(value) && (
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "14px",
              transform: "translateY(-50%)",
              pointerEvents: "none",
              color: disabled
                ? "var(--palette-text-disabled)"
                : "var(--palette-text-placeholder)",
              fontSize: smallSize ? "12px" : "16px",
              zIndex: 1,
            }}
          >
            {label || placeholder}
          </div>
        )}
      </div>
    </LocalizationProvider>
  );
};

export default RpDatePicker;
